import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Blockquote,
    Title,
    Flex,
    Paper,
    Button,
    Image,
    Divider,
    PaperProps,
    Group,
    Skeleton,
    Text,
    Center, ThemeIcon, Stack, Anchor
} from '@mantine/core';
import {IconInfoCircle, IconCircleCheck} from "@tabler/icons-react";
import ImageWithSkeleton from "../ImageWithSkeleton";
import {DuplicateImageGroup, DuplicateImagesResponse, ImageData} from "../../types/Image";
import { shouldLoadMore } from '../gallery/Utils';
import { fetchAllDuplicates, mergeDuplicates, fetchImageDetails } from "../../api/Image";
import {Link} from "react-router-dom";

export function DeleteDuplicates() {
    const [loading, setLoading] = useState(false);
    const [duplicateGroups, setDuplicateGroups] = useState<DuplicateImageGroup[]>([]);
    
    // Track which images have thumbnails loaded - use ref to avoid re-renders
    const imagesWithThumbnails = useRef<Set<string>>(new Set());
    // Track in-flight thumbnail requests
    const inFlightImageRequests = useRef<Set<string>>(new Set());
    // Track visible images to load thumbnails for
    const [visibleImageKeys, setVisibleImageKeys] = useState<Set<string>>(new Set());
    // Observer for tracking visible images
    const imageObserver = useRef<IntersectionObserver | null>(null);
    // Ref for image elements
    const imageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    // Debug counter to track number of thumbnail requests
    const thumbnailRequestCount = useRef<number>(0);

    // Load all duplicate groups
    const loadDuplicateGroups = useCallback(async () => {
        setLoading(true);

        try {
            const response = await fetchAllDuplicates();
            if (response.duplicates.length > 0) {
                setDuplicateGroups(response.duplicates);
                
                // Track which images already have thumbnails
                response.duplicates.forEach(group => {
                    group.forEach(image => {
                        if (image.thumbnail_url) {
                            imagesWithThumbnails.current.add(image.key);
                        }
                    });
                });
            }
        } catch (error) {
            console.error('Error fetching duplicate groups:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDuplicateGroups();
    }, [loadDuplicateGroups]);

    // Setup intersection observer to track which images are visible
    useEffect(() => {
        // Create a new intersection observer only once
        if (!imageObserver.current) {
            console.log('[Observer] Creating new IntersectionObserver');
            imageObserver.current = new IntersectionObserver(
                (entries) => {
                    // Use a function to update state to avoid stale closures
                    setVisibleImageKeys(prevKeys => {
                        const visibleKeys = new Set(prevKeys);
                        
                        entries.forEach(entry => {
                            const imageKey = entry.target.getAttribute('data-image-key');
                            if (imageKey) {
                                if (entry.isIntersecting) {
                                    console.log(`[Observer] Image ${imageKey} is now visible`);
                                    visibleKeys.add(imageKey);
                                } else {
                                    console.log(`[Observer] Image ${imageKey} is no longer visible`);
                                    visibleKeys.delete(imageKey);
                                }
                            }
                        });
                        
                        return visibleKeys;
                    });
                },
                { threshold: 0.1, rootMargin: '100px' } // 10% of the element is visible, with 100px margin for preloading
            );
        }

        return () => {
            // Don't disconnect on every render, only when component unmounts
            // This will be handled by the cleanup function of the parent useEffect
        };
    }, []); // Empty dependency array means this only runs once on mount
    
    // Observe new image elements when duplicateGroups changes
    useEffect(() => {
        // Skip if we don't have an observer yet
        if (!imageObserver.current) return;
        
        console.log('[Observer] Updating observed elements after duplicateGroups change');
        
        // Observe all image elements that aren't already being observed
        Object.entries(imageRefs.current).forEach(([key, element]) => {
            if (element && !element.getAttribute('data-observed') && imageObserver.current) {
                element.setAttribute('data-observed', 'true');
                element.setAttribute('data-image-key', key);
                imageObserver.current.observe(element);
                console.log(`[Observer] Now observing image ${key}`);
            }
        });
        
        return () => {
            // Don't disconnect on every render
        };
    }, [duplicateGroups]);
    
    // Cleanup observer on unmount
    useEffect(() => {
        return () => {
            if (imageObserver.current) {
                console.log('[Observer] Disconnecting IntersectionObserver on unmount');
                imageObserver.current.disconnect();
            }
        };
    }, []);

    // Load thumbnails for visible images
    const loadThumbnailsForVisibleImages = useCallback(async () => {
        if (visibleImageKeys.size === 0) return;

        // Get all image keys that are visible, don't have thumbnails yet, and are not already being fetched
        const imagesToLoad: string[] = [];

        visibleImageKeys.forEach(key => {
            if (!imagesWithThumbnails.current.has(key) && !inFlightImageRequests.current.has(key)) {
                imagesToLoad.push(key);
                inFlightImageRequests.current.add(key);
            }
        });

        if (imagesToLoad.length === 0) return;

        // Increment request counter for debugging
        thumbnailRequestCount.current += 1;
        console.log(`[Thumbnail Request #${thumbnailRequestCount.current}] Loading thumbnails for ${imagesToLoad.length} images:`, imagesToLoad);

        try {
            // Fetch image details to get thumbnails
            const response = await fetchImageDetails(imagesToLoad);
            
            // The response has an image_details object where keys are image IDs and values are thumbnail URLs
            const imageDetails = response.image_details || {};
            const thumbnailCount = Object.keys(imageDetails).length;
            
            console.log(`[Thumbnail Response #${thumbnailRequestCount.current}] Received thumbnails for ${thumbnailCount} images:`, imageDetails);
            
            if (thumbnailCount === 0) {
                console.warn(`[Thumbnail Warning #${thumbnailRequestCount.current}] No thumbnails returned in response`);
                return;
            }
            
            // Update the duplicate groups with the new thumbnail URLs
            const updatedDuplicateGroups = duplicateGroups.map((group: DuplicateImageGroup) => {
                return group.map((image: ImageData) => {
                    // Check if this image has a thumbnail in the response
                    const thumbnailUrl = imageDetails[image.key];
                    if (thumbnailUrl) {
                        // Add to our set of images with thumbnails
                        imagesWithThumbnails.current.add(image.key);
                        // Return updated image with thumbnail
                        return { ...image, thumbnail_url: thumbnailUrl };
                    }
                    return image;
                });
            });
            
            // Update the duplicate groups with the new thumbnails
            setDuplicateGroups(updatedDuplicateGroups);
        } catch (error) {
            console.error(`[Thumbnail Error #${thumbnailRequestCount.current}] Error loading thumbnails:`, error);
        } finally {
            // Remove the image keys from the in-flight set
            imagesToLoad.forEach(key => {
                inFlightImageRequests.current.delete(key);
            });
        }
    }, [visibleImageKeys, duplicateGroups]);

    // Load thumbnails when visible images change
    useEffect(() => {
        loadThumbnailsForVisibleImages();
    }, [loadThumbnailsForVisibleImages]);

    // Handle merging duplicates
    const handleMergeDuplicates = async (group: DuplicateImageGroup) => {
        if (group.length === 0) return;
        
        // The image to keep is the first one in the array
        const keepImageKey = group[0].key;
        
        // Remove this group from the UI immediately for better UX
        setDuplicateGroups(prevGroups => 
            prevGroups.filter(g => g[0].key !== keepImageKey)
        );

        try {
            await mergeDuplicates(keepImageKey);
            console.log(`Successfully merged duplicates for image ${keepImageKey}`);
        } catch (error) {
            console.error('Error merging duplicates:', error);
            // Put the group back if there was an error
            setDuplicateGroups(currentGroups => [...currentGroups, group]);
            alert('Failed to merge duplicates. Please try again.');
        }
    };

    // Function to handle keeping all images (skip this duplicate group)
    const handleKeepAll = (group: DuplicateImageGroup) => {
        if (group.length === 0) return;
        
        // Simply remove this group from the UI
        setDuplicateGroups(prevGroups => 
            prevGroups.filter(g => g[0].key !== group[0].key)
        );
    };

    const DuplicateGroupsSkeleton = () => (
        <div>
            {[...Array(3)].map((_, idx) => (
                <Paper key={idx} shadow="sm" radius="lg" withBorder p="xl" mt={idx > 0 ? 'lg' : undefined}>
                    <Skeleton height={28} width="40%" radius="xl" />
                    <Divider mt="sm" />
                    <Flex mt="lg" wrap="wrap" gap="5px">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} width="calc((100% - 15px) / 3)" radius="md" style={{ aspectRatio: 1 }} />
                        ))}
                    </Flex>
                    <Group justify="flex-end" gap="sm" mt="lg">
                        <Skeleton height={36} width={100} radius="md" />
                        <Skeleton height={36} width={140} radius="md" />
                    </Group>
                </Paper>
            ))}
        </div>
    );

    return (
        <Flex direction="column">
            <Title order={1}>Remove Duplicates</Title>
            <Divider mt="xs"/>
            <Blockquote color="blue" icon={<IconInfoCircle/>} mt="lg" mb="lg">
                Deleting duplicates ensures a clutter-free photo collection while merging all relevant metadata from
                duplicate photos.
            </Blockquote>
            {loading && <DuplicateGroupsSkeleton/>}
            {!loading && duplicateGroups.length === 0 ? (
                <Paper w="100%" mt="md" shadow="md" radius="md" withBorder p="xl">
                    <Stack align="center" gap="md">
                        <ThemeIcon size="xl" radius="xl" color="blue">
                            <IconCircleCheck size={32}/>
                        </ThemeIcon>
                        <Text size="lg" w={600} ta="center">
                            Looks like you don't have any duplicates! 🎉
                        </Text>
                        <Text c="gray.6" ta="center">
                            <Anchor component={Link} to="/import" underline="always">Import</Anchor> more photos and keep your photo collection clutter-free.
                        </Text>
                    </Stack>
                </Paper>
            ) : (
                duplicateGroups.map(group => {
                    // Skip empty groups
                    if (group.length === 0) return null;
                    
                    // Use the first image's key as the group identifier
                    const mainImageKey = group[0].key;
                    
                    return (
                        <Paper key={mainImageKey} shadow="sm" radius="lg" withBorder p="xl" mt="lg">
                            <Title order={2}>Duplicate Images</Title>
                            <Divider mt="sm"/>
                            <Flex mt="lg" wrap="wrap" gap="5px">
                                {group.map((image, idx) => (
                                    <div 
                                        key={image.key} 
                                        ref={el => {
                                            // Store reference to this element
                                            imageRefs.current[image.key] = el;
                                            // We'll handle observation in the useEffect to avoid doing it during render
                                        }}
                                        style={{width: 'calc((100% - 15px) / 3)', aspectRatio: 1}}
                                        data-image-key={image.key}
                                    >
                                        {image.thumbnail_url ? (
                                            <Image 
                                                src={image.thumbnail_url} 
                                                alt={`Duplicate ${idx === 0 ? '(Main)' : idx}`}
                                                style={{width: '100%', height: '100%', objectFit: 'cover'}}
                                            />
                                        ) : (
                                            <Skeleton width="100%" height="100%" radius="md" />
                                        )}
                                    </div>
                                ))}
                            </Flex>
                            <Group justify="flex-end" gap="sm" mt="lg">
                                <Button color="gray" variant="outline" onClick={() => handleKeepAll(group)}>
                                    Keep All
                                </Button>
                                <Button color="blue" onClick={() => handleMergeDuplicates(group)}>
                                    Merge Duplicates
                                </Button>
                            </Group>
                        </Paper>
                    );
                })
            )}
        </Flex>
    );
}

export default DeleteDuplicates;